import { Purchases, LOG_LEVEL, PACKAGE_TYPE } from '@revenuecat/purchases-capacitor';
import { Capacitor } from '@capacitor/core';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db, auth } from './firebase';

// TODO: Replace with your actual RevenueCat API Keys from the Dashboard
const API_KEYS = {
    ios: "appl_LwZgVLVHcKYoAZnuppKDCTEOpGX",
    android: "goog_NTkussBcjcwSkozMTaMvyfMBYpQ"
};

const ENTITLEMENT_ID = "pro_access"; 

// Helper to distinguish web vs native
const isWeb = () => Capacitor.getPlatform() === 'web';

export const initializePurchases = async (userId) => {
    if (isWeb()) {
        // log("RevenueCat: Web detected. Native purchases skipped.");
        return;
    }

    try {
        if (Capacitor.getPlatform() === 'ios') {
            await Purchases.configure({ apiKey: API_KEYS.ios, appUserID: userId });
        } else if (Capacitor.getPlatform() === 'android') {
            await Purchases.configure({ apiKey: API_KEYS.android, appUserID: userId });
        }
        await Purchases.setLogLevel({ level: LOG_LEVEL.DEBUG });

        // --- NEW: GLOBAL LISTENER FOR STATUS CHANGES ---
        // This catches purchases even if the UI crashed or restarted during the process.
        await Purchases.addCustomerInfoUpdateListener(async (info) => {
            // log("RevenueCat Listener Fired:", info);
            
            // Check if Entitlement is Active
            const entitlements = info.customerInfo.entitlements.active;
            const isActive = typeof entitlements[ENTITLEMENT_ID] !== "undefined";

            if (isActive && userId) {
                // log("Listener found active Pro entitlement. Syncing to Firestore...");
                try {
                    // Use setDoc with merge to be safe (creates doc if missing)
                    const userRef = doc(db, 'users', userId);
                    await setDoc(userRef, { 
                        isPro: true, 
                        proSince: new Date() 
                    }, { merge: true });
                    // log("Firestore synced successfully from Listener.");
                } catch(err) {
                    // error("Firestore sync error in listener:", err);
                }
            }
        });

    } catch (e) {
        // error("RevenueCat Init Error:", e);
    }
};

export const getOfferings = async () => {
    // 1. WEB MOCK
    if (isWeb()) {
        return [
            { 
                identifier: 'pro_monthly', 
                product: { priceString: '$4.99', title: 'Pro Monthly', description: 'Unlock advanced AI & unlimited sessions', productType: 'SUBS' },
                packageType: 'MONTHLY'
            },
            {
                identifier: 'logic_bomb_pack',
                product: { priceString: '$0.99', title: 'Logic Bomb', description: 'Blast a gavel reaction.', productType: 'INAPP' }
            }
        ];
    }

    // 2. NATIVE REAL FETCH
    try {
        const offerings = await Purchases.getOfferings();
        if (offerings.current !== null && offerings.current.availablePackages.length !== 0) {
            return offerings.current.availablePackages;
        }
        return [];
    } catch (e) {
        // error("Error fetching offerings:", e);
        return [];
    }
};

export const purchasePackage = async (rcPackage) => {
    // log("Processing purchase for:", rcPackage.identifier);

    // 1. WEB MOCK
    if (isWeb()) {
        await new Promise(resolve => setTimeout(resolve, 1500));
        if (auth.currentUser) {
            try {
                const userRef = doc(db, 'users', auth.currentUser.uid);
                
                // MOCK LOGIC: Check if subscription or consumable
                if (rcPackage.product.productType === 'SUBS' || rcPackage.identifier.includes('monthly')) {
                    await setDoc(userRef, { 
                        isPro: true,
                        proSince: new Date(),
                        subscriptionStatus: 'active'
                    }, { merge: true });
                    // log("SIMULATION: Pro status granted.");
                } else {
                    // Guess inventory key from identifier for mock
                    let key = 'logic_bomb';
                    if (rcPackage.identifier.includes('evidence')) key = 'evidence_bomb';
                    if (rcPackage.identifier.includes('insight')) key = 'insight_bomb';
                    if (rcPackage.identifier.includes('boost') || rcPackage.identifier.includes('xp')) key = 'xp_boost_24h';
                    
                    await setDoc(userRef, { 
                        inventory: { [key]: increment(5) } 
                    }, { merge: true });
                    // log(`SIMULATION: Added 5 ${key}.`);
                }
            } catch (e) { /* simulation error */ }
        }
        return { 
            entitlements: { active: { [ENTITLEMENT_ID]: true } },
            customerInfo: { entitlements: { all: { [ENTITLEMENT_ID]: { isActive: true } } } }
        };
    }

    // 2. NATIVE REAL PURCHASE
    try {
        const { customerInfo } = await Purchases.purchasePackage({ aPackage: rcPackage });
        // log("Purchase successful. CustomerInfo:", customerInfo);
        
        // A. HANDLE SUBSCRIPTIONS (Entitlement Check)
        if (typeof customerInfo.entitlements.active[ENTITLEMENT_ID] !== "undefined") {
            // log("Entitlement active! Updating Firestore...");
            // FORCE UPDATE USING CURRENT USER ID
            const uid = auth.currentUser?.uid;
            
            if (uid) {
                await setDoc(doc(db, 'users', uid), { 
                    isPro: true, 
                    proSince: new Date() 
                }, { merge: true });
                // log("Firestore updated with isPro: true");
            } else {
                // error("No auth.currentUser found to update Firestore!");
            }
        } else {
            // warn(`Purchase successful but entitlement '${ENTITLEMENT_ID}' is NOT active.`);
        }
        
        // B. HANDLE CONSUMABLES (Manual Inventory Update)
        // RevenueCat doesn't "unlock" entitlements for consumables, so we check product ID manually here.
        
        if (auth.currentUser) {
            const pid = rcPackage.product.identifier.toLowerCase();
            // log("Checking consumable logic for:", pid);
            
            let inventoryKey = null;

            if (pid.includes('logic')) inventoryKey = 'logic_bomb';
            else if (pid.includes('evidence')) inventoryKey = 'evidence_bomb';
            else if (pid.includes('insight')) inventoryKey = 'insight_bomb';
            else if (pid.includes('boost') || pid.includes('xp')) inventoryKey = 'xp_boost_24h';
            
            if (inventoryKey) {
                // log(`Granting consumable: ${inventoryKey}`);
                const userRef = doc(db, 'users', auth.currentUser.uid);
                await updateDoc(userRef, { 
                    [`inventory.${inventoryKey}`]: increment(5) 
                });
            } else if (pid.includes('title')) {
                 // Clean up title (remove "Title " prefix and store artifact text)
                 // e.g. "Title Baron (Verbi)" -> "Baron"
                 const rawTitle = rcPackage.product.title;
                 const cleanTitle = rawTitle
                    .replace(/Title\s+/i, '') // Remove "Title " prefix
                    .replace(/\(Verbi\)|android/gi, '')
                    .trim();

                 // log("Granting Title:", cleanTitle);
                 await updateDoc(doc(db, 'users', auth.currentUser.uid), {
                     purchasedTitle: cleanTitle
                 });
            }
        }

        return customerInfo;
    } catch (e) {
        if (e.userCancelled) throw new Error("Cancelled");
        // error("Purchase failed:", e);
        throw e;
    }
};

export const checkProStatus = async () => {
    if (!auth.currentUser) return false;

    // 1. Check Firestore (Fastest for UI)
    try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const snap = await getDoc(userRef);
        // If Firestore says true, trust it temporarily to prevent UI flicker
        if (snap.exists() && snap.data().isPro === true) {
            return true;
        }
    } catch (e) {
        // warn("Firestore check failed", e);
    }

    // 2. Fallback to RevenueCat (Native Only Source of Truth)
    if (!isWeb()) {
        try {
            const customerInfo = await Purchases.getCustomerInfo();
            const isActive = typeof customerInfo.entitlements.active[ENTITLEMENT_ID] !== "undefined";
            
            // Sync back to Firestore if discrepency found (Native says YES, Firestore says NO/Missing)
            if (isActive && auth.currentUser) {
                // FORCE UPDATE
                await setDoc(doc(db, 'users', auth.currentUser.uid), { isPro: true }, { merge: true });
            }
            return isActive;
        } catch (e) {
            // error("RevenueCat check failed:", e);
        }
    }

    return false;
};

export const restorePurchases = async () => {
    if (isWeb()) return await checkProStatus();

    try {
        const customerInfo = await Purchases.restorePurchases();
        const isActive = typeof customerInfo.entitlements.active[ENTITLEMENT_ID] !== "undefined";
        
        if (isActive && auth.currentUser) {
            await setDoc(doc(db, 'users', auth.currentUser.uid), { isPro: true }, { merge: true });
        }
        return isActive;
    } catch (e) {
        // error("Restore failed:", e);
        throw e;
    }
};

// --- CONSUMABLE PURCHASES (DIRECT) ---
// Kept for fallback if you use purchaseConsumable instead of purchasePackage
export const purchaseConsumable = async (storeProductId) => {
    // log("Processing consumable purchase:", storeProductId);

    // 1. WEB MOCK
    if (isWeb()) {
        // ... (Mock logic same as above)
        return true;
    }

    // 2. NATIVE REAL PURCHASE
    try {
        const products = await Purchases.getProducts({ productIdentifiers: [storeProductId] });
        if (products.length === 0) throw new Error("Product not found in store");

        await Purchases.purchaseStoreProduct({ product: products[0] });
        
        // GRANT ITEM
        if (auth.currentUser) {
            const pid = storeProductId.toLowerCase();
            let inventoryKey = null;

            if (pid.includes('logic')) inventoryKey = 'logic_bomb';
            else if (pid.includes('evidence')) inventoryKey = 'evidence_bomb';
            else if (pid.includes('insight')) inventoryKey = 'insight_bomb';
            else if (pid.includes('boost') || pid.includes('xp')) inventoryKey = 'xp_boost_24h';

            if (inventoryKey) {
                const userRef = doc(db, 'users', auth.currentUser.uid);
                await updateDoc(userRef, { 
                    [`inventory.${inventoryKey}`]: increment(5) 
                });
            }
        }
        return true;
    } catch (e) {
        // error("Consumable Purchase Error:", e);
        if (e.userCancelled) return false;
        throw e;
    }
};